import React, { useState, useRef, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Tester de VOZ no navegador: o microfone conecta direto na ponte de áudio do
// agente (mesma WS /agent-media do jambonz). Orbe reativo ao áudio REAL —
// amplitude da fala da IA (estado "Falando") e do seu mic (estado "Ouvindo").

const useStyles = makeStyles({
  paper: { width: "min(92vw, 380px)", height: "min(84vh, 600px)", borderRadius: 30, overflow: "hidden", background: "radial-gradient(120% 80% at 50% 30%,#19222a 0%,#0d1114 60%,#0a0d10 100%)", border: "1px solid rgba(255,255,255,.06)", position: "relative", margin: 16 },
  status: { position: "absolute", top: 30, left: 0, right: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 13, color: "#aeb6bf", letterSpacing: ".02em", zIndex: 2 },
  dot: { width: 8, height: 8, borderRadius: "50%", transition: ".3s" },
  canvas: { position: "absolute", inset: 0, width: "100%", height: "100%" },
  cap: { position: "absolute", bottom: 118, left: 0, right: 0, textAlign: "center", color: "#cfd6dd", fontSize: 14, padding: "0 40px", lineHeight: 1.5, zIndex: 2, minHeight: 44 },
  ctrls: { position: "absolute", bottom: 34, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 9, zIndex: 2 },
  btn: { fontSize: 12.5, padding: "9px 16px", borderRadius: 20, border: "1px solid rgba(255,255,255,.14)", background: "rgba(255,255,255,.04)", color: "#cfd6dd", cursor: "pointer", fontFamily: "inherit" },
  btnGo: { background: "#CEFF00", color: "#10140f", borderColor: "#CEFF00", fontWeight: 600 },
  btnEnd: { background: "#C92A2A", color: "#fff", borderColor: "#C92A2A", fontWeight: 600 },

  gear: {
    position: "absolute", top: 22, right: 20, zIndex: 3, width: 30, height: 30, borderRadius: "50%",
    border: "1px solid rgba(255,255,255,.14)", background: "rgba(255,255,255,.04)", color: "#aeb6bf",
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
    "&:hover": { background: "rgba(255,255,255,.1)", color: "#e6eaf0" },
  },
  cfg: {
    position: "absolute", left: 16, right: 16, top: 62, zIndex: 4, padding: "14px 16px 16px",
    borderRadius: 16, background: "rgba(16,21,26,.97)", border: "1px solid rgba(255,255,255,.1)",
    boxShadow: "0 16px 40px rgba(0,0,0,.5)",
  },
  cfgTitle: { fontSize: 12.5, fontWeight: 600, color: "#e6eaf0", marginBottom: 12 },
  cfgLabel: { display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: "#8d97a1", margin: "12px 0 5px" },
  select: {
    width: "100%", height: 34, padding: "0 8px", borderRadius: 8, fontSize: 12.5, fontFamily: "inherit",
    background: "rgba(255,255,255,.06)", color: "#e6eaf0", border: "1px solid rgba(255,255,255,.14)",
    "&:disabled": { opacity: 0.45, cursor: "default" },
  },
  cfgHint: { fontSize: 11, color: "#78828c", marginTop: 6, lineHeight: 1.45 },
  cfgClose: { marginTop: 14, width: "100%", fontSize: 12.5, padding: "8px 0", borderRadius: 10, border: "1px solid rgba(255,255,255,.14)", background: "rgba(255,255,255,.06)", color: "#cfd6dd", cursor: "pointer", fontFamily: "inherit" },
});

// A saída de áudio só é selecionável onde existe AudioContext.setSinkId
// (Chromium 110+). Firefox e Safari não expõem — o seletor fica desabilitado
// em vez de fingir que trocou.
const canPickOutput = () =>
  typeof window !== "undefined" &&
  typeof window.AudioContext !== "undefined" &&
  typeof window.AudioContext.prototype.setSinkId === "function";

const LS_MIC = "numera.voiceTester.micId";
const LS_OUT = "numera.voiceTester.outId";

const BLOBS = [
  { col: [206, 255, 0], n: [{ f: 2, s: 0.7, p: 0 }, { f: 3, s: -1.1, p: 1.3 }, { f: 5, s: 0.5, p: 2.1 }], rot: 0.25, base: 0.92, a: 0.55 },
  { col: [151, 185, 188], n: [{ f: 3, s: -0.6, p: 0.5 }, { f: 2, s: 0.9, p: 2.2 }, { f: 4, s: -0.4, p: 4 }], rot: -0.4, base: 1.0, a: 0.5 },
  { col: [190, 255, 90], n: [{ f: 4, s: 0.5, p: 1 }, { f: 2, s: -0.8, p: 3 }, { f: 6, s: 0.35, p: 0.4 }], rot: 0.6, base: 0.82, a: 0.42 },
];
const NP = 72;
const noise = (b, ang, t) => { let s = 0; for (const o of b.n) s += Math.sin(o.f * ang + o.s * t + o.p); return s / b.n.length; };
function drawBlob(ctx, b, t, cx, cy, R, dist, glow) {
  const pts = [];
  for (let i = 0; i < NP; i += 1) { const ang = (i / NP) * Math.PI * 2; const rr = R * (b.base + dist * noise(b, ang + b.rot * t, t)); pts.push([cx + Math.cos(ang) * rr, cy + Math.sin(ang) * rr]); }
  ctx.beginPath();
  ctx.moveTo((pts[NP - 1][0] + pts[0][0]) / 2, (pts[NP - 1][1] + pts[0][1]) / 2);
  for (let i = 0; i < NP; i += 1) { const nx = pts[(i + 1) % NP]; ctx.quadraticCurveTo(pts[i][0], pts[i][1], (pts[i][0] + nx[0]) / 2, (pts[i][1] + nx[1]) / 2); }
  ctx.closePath();
  const g = ctx.createRadialGradient(cx, cy, R * 0.1, cx, cy, R * 1.15);
  g.addColorStop(0, `rgba(${b.col[0]},${b.col[1]},${b.col[2]},${b.a})`);
  g.addColorStop(0.7, `rgba(${b.col[0]},${b.col[1]},${b.col[2]},${b.a * 0.5})`);
  g.addColorStop(1, `rgba(${b.col[0]},${b.col[1]},${b.col[2]},0)`);
  ctx.fillStyle = g; ctx.shadowBlur = glow; ctx.shadowColor = `rgba(${b.col[0]},${b.col[1]},${b.col[2]},.7)`;
  ctx.fill(); ctx.shadowBlur = 0;
}

function downsampleToInt16(f32, srcRate, dstRate) {
  const ratio = srcRate / dstRate;
  const outLen = Math.floor(f32.length / ratio);
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i += 1) { const s = Math.max(-1, Math.min(1, f32[Math.floor(i * ratio)] || 0)); out[i] = s < 0 ? s * 0x8000 : s * 0x7fff; }
  return out;
}

const STATUS = { idle: "Parado", listen: "Ouvindo", speak: "Falando" };
const DOTCOL = { idle: "#6b7681", listen: "#97B9BC", speak: "#CEFF00" };

const AgentVoiceTester = ({ open, onClose, agentId, beforeStart }) => {
  const classes = useStyles();
  const [ui, setUi] = useState("idle"); // idle | listen | speak
  const [live, setLive] = useState(false);
  const [muted, setMuted] = useState(false);
  const [status, setStatus] = useState("Toque em iniciar para falar com o agente.");
  const [showCfg, setShowCfg] = useState(false);
  const [mics, setMics] = useState([]);
  const [outs, setOuts] = useState([]);
  const [micId, setMicId] = useState(() => localStorage.getItem(LS_MIC) || "");
  const [outId, setOutId] = useState(() => localStorage.getItem(LS_OUT) || "");
  const cvRef = useRef(null);
  const r = useRef({ amp: 0.18, aiLevel: 0, micLevel: 0, lastAiAt: 0, shown: "idle" });

  // Lista os dispositivos. Os NOMES só vêm depois de o usuário ter concedido o
  // microfone alguma vez — antes disso o navegador devolve label vazia (é
  // proteção contra fingerprinting). Por isso, ao abrir o painel, pedimos uma
  // permissão relâmpago só para destravar os nomes e soltamos na hora.
  const refreshDevices = React.useCallback(async ({ unlockLabels = false } = {}) => {
    try {
      let list = await navigator.mediaDevices.enumerateDevices();
      const noLabels = list.filter(d => d.kind === "audioinput").every(d => !d.label);
      if (unlockLabels && noLabels) {
        const tmp = await navigator.mediaDevices.getUserMedia({ audio: true });
        tmp.getTracks().forEach(t => t.stop());
        list = await navigator.mediaDevices.enumerateDevices();
      }
      setMics(list.filter(d => d.kind === "audioinput"));
      setOuts(list.filter(d => d.kind === "audiooutput"));
    } catch {
      /* sem permissão / sem suporte: mantém as listas como estão */
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    refreshDevices();
    // fone plugado/desplugado no meio da conversa
    const md = navigator.mediaDevices;
    const onChange = () => refreshDevices();
    if (md && md.addEventListener) md.addEventListener("devicechange", onChange);
    return () => {
      if (md && md.removeEventListener) md.removeEventListener("devicechange", onChange);
    };
  }, [open, refreshDevices]);

  // aplica a saída escolhida no contexto de reprodução (quando o navegador deixa)
  const applyOutput = async (ctx, id) => {
    if (!ctx || !id || !canPickOutput()) return;
    try {
      await ctx.setSinkId(id);
    } catch {
      /* dispositivo sumiu: segue no padrão do sistema */
    }
  };

  // animação do orbe (roda enquanto o diálogo está aberto)
  useEffect(() => {
    if (!open) return undefined;
    let raf;
    let W = 0, H = 0, lastW = 0, lastH = 0;
    const frame = now => {
      const cv = cvRef.current;
      if (!cv) { raf = requestAnimationFrame(frame); return; }
      const ctx = cv.getContext("2d");
      // mede a cada frame (robusto à transição do diálogo); pula até ter layout
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = cv.clientWidth || (cv.parentElement && cv.parentElement.clientWidth) || 0;
      H = cv.clientHeight || (cv.parentElement && cv.parentElement.clientHeight) || 0;
      if (!W || !H) { raf = requestAnimationFrame(frame); return; }
      if (W !== lastW || H !== lastH) { cv.width = W * dpr; cv.height = H * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); lastW = W; lastH = H; }
      const st = r.current;
      const t = now / 1000;
      const speaking = st.lastAiAt && now - st.lastAiAt < 250;
      const state = !st.liveFlag ? "idle" : speaking ? "speak" : st.mutedFlag ? "idle" : "listen";
      let target;
      if (state === "idle") target = 0.16 + 0.05 * Math.sin(t * 1.2);
      else if (state === "speak") target = Math.max(0.22, Math.min(1, st.aiLevel * 3.5));
      else target = Math.max(0.2, Math.min(1, st.micLevel * 4));
      st.amp += (target - st.amp) * 0.12;
      const amp = st.amp;
      if (state !== st.shown) { st.shown = state; setUi(state); }
      // decai os níveis
      st.aiLevel *= 0.9; st.micLevel *= 0.9;

      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "rgba(11,14,17,.28)"; ctx.fillRect(0, 0, W, H);
      const cx = W / 2, cy = H * 0.42, baseR = Math.min(W, H) * 0.2;
      const dist = 0.12 + 0.55 * amp, Rp = baseR * (1 + 0.18 * amp), glow = 18 + 48 * amp;
      ctx.globalCompositeOperation = "lighter";
      ctx.beginPath(); ctx.arc(cx, cy, Rp * (1.35 + 0.1 * amp), 0, 7);
      ctx.strokeStyle = `rgba(206,255,0,${0.05 + 0.08 * amp})`; ctx.lineWidth = 1.5;
      ctx.shadowBlur = 30; ctx.shadowColor = "rgba(206,255,0,.4)"; ctx.stroke(); ctx.shadowBlur = 0;
      for (const b of BLOBS) drawBlob(ctx, b, t, cx, cy, Rp, dist, glow);
      const cg = ctx.createRadialGradient(cx, cy, 0, cx, cy, Rp * 0.5);
      cg.addColorStop(0, `rgba(245,255,220,${0.25 + 0.35 * amp})`); cg.addColorStop(1, "rgba(245,255,220,0)");
      ctx.fillStyle = cg; ctx.beginPath(); ctx.arc(cx, cy, Rp * 0.5, 0, 7); ctx.fill();
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const stopAll = () => {
    const o = r.current;
    try { o.ws && o.ws.close(); } catch (e) { /* */ }
    try { o.proc && o.proc.disconnect(); } catch (e) { /* */ }
    try { o.src && o.src.disconnect(); } catch (e) { /* */ }
    try { o.stream && o.stream.getTracks().forEach(t => t.stop()); } catch (e) { /* */ }
    try { o.capCtx && o.capCtx.close(); } catch (e) { /* */ }
    try { o.playCtx && o.playCtx.close(); } catch (e) { /* */ }
    o.ws = o.proc = o.src = o.stream = o.capCtx = o.playCtx = null;
    o.liveFlag = false; o.aiLevel = 0; o.micLevel = 0;
    setLive(false); setMuted(false);
  };
  const hangup = () => { stopAll(); setStatus("Conversa encerrada."); onClose(); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open) stopAll(); return () => stopAll(); }, [open]);

  // Abre o microfone escolhido. `ideal` em vez de `exact`: se o dispositivo
  // salvo tiver sumido (fone desconectado), o navegador cai no padrão em vez de
  // estourar NotFoundError e deixar o teste sem áudio.
  const openMic = deviceId =>
    navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        ...(deviceId ? { deviceId: { ideal: deviceId } } : {}),
      },
    });

  // Troca o microfone COM A CONVERSA NO AR: derruba só a captura (stream, ctx,
  // processor) e remonta apontando pro novo dispositivo. O WebSocket e a
  // reprodução seguem intactos — a conversa não cai.
  const swapMic = async deviceId => {
    const o = r.current;
    if (!o.liveFlag || !o.ws) return;
    try { o.proc && o.proc.disconnect(); } catch (e) { /* */ }
    try { o.src && o.src.disconnect(); } catch (e) { /* */ }
    try { o.stream && o.stream.getTracks().forEach(t => t.stop()); } catch (e) { /* */ }
    try { o.capCtx && o.capCtx.close(); } catch (e) { /* */ }
    try {
      const stream = await openMic(deviceId);
      const capCtx = new (window.AudioContext || window.webkitAudioContext)();
      await capCtx.resume().catch(() => undefined);
      const src = capCtx.createMediaStreamSource(stream);
      const proc = capCtx.createScriptProcessor(4096, 1, 1);
      const sink = capCtx.createGain(); sink.gain.value = 0;
      src.connect(proc); proc.connect(sink); sink.connect(capCtx.destination);
      proc.onaudioprocess = o.onCapture(capCtx);
      Object.assign(o, { stream, capCtx, src, proc });
    } catch (err) {
      setStatus("Não consegui abrir esse microfone.");
    }
  };

  const changeMic = async id => {
    setMicId(id);
    localStorage.setItem(LS_MIC, id);
    await swapMic(id);
  };

  const changeOut = async id => {
    setOutId(id);
    localStorage.setItem(LS_OUT, id);
    await applyOutput(r.current.playCtx, id); // aplica na hora, sem derrubar
  };

  const start = async () => {
    setStatus("Preparando…");
    try {
      if (beforeStart) await beforeStart();
      const { data } = await api.post(`/applications/${agentId}/web-voice`);
      const inRate = data.inputSampleRate || 16000;
      const outRate = data.outputSampleRate || 24000;
      setStatus("Pedindo o microfone…");
      const stream = await openMic(micId);
      // com a permissão concedida, os nomes dos dispositivos ficam disponíveis
      refreshDevices();

      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(`${proto}//${window.location.host}/agent-media/${data.sessionId}`);
      ws.binaryType = "arraybuffer";
      const playCtx = new (window.AudioContext || window.webkitAudioContext)();
      try { await playCtx.resume(); } catch (e) { /* */ }
      await applyOutput(playCtx, outId);
      let cursor = 0; let queued = [];
      const capCtx = new (window.AudioContext || window.webkitAudioContext)();
      try { await capCtx.resume(); } catch (e) { /* */ }
      const src = capCtx.createMediaStreamSource(stream);
      const proc = capCtx.createScriptProcessor(4096, 1, 1);
      const sink = capCtx.createGain(); sink.gain.value = 0;
      src.connect(proc); proc.connect(sink); sink.connect(capCtx.destination);

      const o = r.current;
      Object.assign(o, { ws, stream, capCtx, playCtx, src, proc, liveFlag: true, mutedFlag: false });

      // fábrica guardada no ref: trocar de microfone remonta o processor e
      // precisa do MESMO handler, mas apontando pro novo contexto — o sample
      // rate pode mudar de um dispositivo para outro.
      o.onCapture = ctx => e => {
        const f32 = e.inputBuffer.getChannelData(0);
        let sum = 0; for (let i = 0; i < f32.length; i += 1) sum += f32[i] * f32[i];
        o.micLevel = Math.max(o.micLevel, Math.sqrt(sum / f32.length));
        if (ws.readyState !== 1 || o.mutedFlag) return;
        try { ws.send(downsampleToInt16(f32, ctx.sampleRate, inRate).buffer); } catch (err) { /* */ }
      };
      proc.onaudioprocess = o.onCapture(capCtx);
      ws.onopen = () => { setLive(true); setStatus("Fale com o agente."); };
      ws.onmessage = ev => {
        if (typeof ev.data === "string") {
          try { if (JSON.parse(ev.data).type === "killAudio") { queued.forEach(s => { try { s.stop(); } catch (e) { /* */ } }); queued = []; cursor = playCtx.currentTime; } } catch (e) { /* */ }
          return;
        }
        const i16 = new Int16Array(ev.data);
        let sum = 0; for (let i = 0; i < i16.length; i += 1) { const v = i16[i] / 0x8000; sum += v * v; }
        o.aiLevel = Math.max(o.aiLevel, Math.sqrt(sum / i16.length)); o.lastAiAt = performance.now();
        const buf = playCtx.createBuffer(1, i16.length, outRate);
        const ch = buf.getChannelData(0); for (let i = 0; i < i16.length; i += 1) ch[i] = i16[i] / 0x8000;
        const node = playCtx.createBufferSource(); node.buffer = buf; node.connect(playCtx.destination);
        const at = Math.max(playCtx.currentTime, cursor); node.start(at); cursor = at + buf.duration;
        queued.push(node); node.onended = () => { queued = queued.filter(q => q !== node); };
      };
      ws.onclose = () => { setStatus("Conversa encerrada."); setLive(false); o.liveFlag = false; };
      ws.onerror = () => setStatus("Erro de conexão de áudio.");
    } catch (err) {
      setStatus("");
      if (err && err.name === "NotAllowedError") toastError({ response: { data: { error: "Permita o microfone para testar por voz." } } });
      else toastError(err);
    }
  };

  const toggleMute = () => { const o = r.current; o.mutedFlag = !o.mutedFlag; setMuted(o.mutedFlag); };

  return (
    <Dialog open={open} onClose={hangup} classes={{ paper: classes.paper }}>
      <div className={classes.status}>
        <span className={classes.dot} style={{ background: DOTCOL[ui], boxShadow: `0 0 10px ${DOTCOL[ui]}` }} />
        <span>{STATUS[ui]}</span>
      </div>

      <button
        className={classes.gear}
        title="Microfone e som"
        onClick={() => { setShowCfg(v => !v); if (!showCfg) refreshDevices({ unlockLabels: true }); }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      {showCfg && (
        <div className={classes.cfg}>
          <div className={classes.cfgTitle}>Microfone e som</div>

          <label className={classes.cfgLabel} htmlFor="vt-mic">Microfone</label>
          <select
            id="vt-mic"
            className={classes.select}
            value={micId}
            onChange={e => changeMic(e.target.value)}
          >
            <option value="">Padrão do sistema</option>
            {mics.map((d, i) => (
              <option key={d.deviceId || i} value={d.deviceId}>
                {d.label || `Microfone ${i + 1}`}
              </option>
            ))}
          </select>

          <label className={classes.cfgLabel} htmlFor="vt-out">Saída de áudio</label>
          <select
            id="vt-out"
            className={classes.select}
            value={outId}
            disabled={!canPickOutput()}
            onChange={e => changeOut(e.target.value)}
          >
            <option value="">Padrão do sistema</option>
            {outs.map((d, i) => (
              <option key={d.deviceId || i} value={d.deviceId}>
                {d.label || `Saída ${i + 1}`}
              </option>
            ))}
          </select>
          {!canPickOutput() && (
            <div className={classes.cfgHint}>
              Seu navegador não permite escolher a saída de áudio por aqui. Troque o
              dispositivo padrão no sistema — o Chrome e o Edge suportam a seleção.
            </div>
          )}

          <div className={classes.cfgHint}>
            {live
              ? "A troca vale na hora, sem derrubar a conversa."
              : "A escolha fica salva para os próximos testes."}
          </div>

          <button className={classes.cfgClose} onClick={() => setShowCfg(false)}>Fechar</button>
        </div>
      )}
      <canvas ref={cvRef} className={classes.canvas} />
      <div className={classes.cap}>{status}</div>
      <div className={classes.ctrls}>
        {!live ? (
          <button className={`${classes.btn} ${classes.btnGo}`} onClick={start}>Iniciar conversa</button>
        ) : (
          <>
            <button className={classes.btn} onClick={toggleMute}>{muted ? "Reativar mic" : "Mudo"}</button>
            <button className={`${classes.btn} ${classes.btnEnd}`} onClick={hangup}>Encerrar</button>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default AgentVoiceTester;
